<template>
  <v-main>
    <v-container fluid class="pa-6 pos-page">
      <!-- Header -->
      <div class="pos-head">
        <v-btn icon class="mr-2" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="pos-crumb">Job Positions</div>
          <h1 class="pos-title">{{ form.name || "Edit Position" }}</h1>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          :loading="isLoading"
          :disabled="isLoading || isFetching"
          @click="save"
        >
          <v-icon left>mdi-content-save-outline</v-icon>Save Changes
        </v-btn>
      </div>

      <page-loader :loading="isFetching" />

      <!-- Details -->
      <section class="pos-card">
        <h2 class="pos-card__title">Position details</h2>
        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="form.name"
              outlined
              dense
              label="Position name"
              prepend-inner-icon="mdi-badge-account-outline"
              :hint="isPanelPosition ? 'Panel access is tied to this name, so keep it as it is' : ''"
              persistent-hint
              :readonly="isPanelPosition"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="form.is_active"
              inset
              class="mt-1"
              :label="form.is_active ? 'Active' : 'Inactive'"
            />
          </v-col>
        </v-row>
      </section>

      <!-- Panel access -->
      <section v-if="access" class="pos-card">
        <div class="pos-access-head">
          <div>
            <h2 class="pos-card__title mb-1">Admin panel access</h2>
            <p class="pos-muted mb-0" v-if="access.is_panel_position">
              Choose what a <strong>{{ titleCase(form.name) }}</strong> can do on each screen of the
              admin panel. Screens set to "No access" are hidden from their menu.
            </p>
          </div>
          <div v-if="access.is_panel_position" class="pos-count">
            <strong>{{ grantedCount }}</strong> of {{ totalCount }} screens
          </div>
        </div>

        <v-alert type="info" text dense class="mt-4 mb-0" icon="mdi-shield-crown-outline">
          <strong>Admin</strong> and <strong>Super Admin</strong> always have full access to every screen.
        </v-alert>

        <!-- What each level allows -->
        <div v-if="access.is_panel_position" class="pos-legend">
          <div v-for="lvl in legend" :key="lvl.value" class="pos-legend__item">
            <span class="pos-level-dot" :class="`is-${lvl.value}`"></span>
            <strong>{{ lvl.text }}</strong>
            <span class="pos-muted">{{ lvl.help }}</span>
          </div>
        </div>

        <!-- App-only position -->
        <div v-if="!access.is_panel_position" class="pos-empty">
          <v-icon size="40" color="grey lighten-1">mdi-cellphone</v-icon>
          <h3>{{ titleCase(form.name) }} uses the mobile app</h3>
          <p class="pos-muted">
            This position does not sign in to the admin panel, so there is nothing to set here.
            Panel access can be given to
            <strong>{{ access.panel_positions.map(titleCase).join(" and ") }}</strong>.
          </p>
        </div>

        <template v-else>
          <!-- Toolbar -->
          <div class="pos-toolbar">
            <v-text-field
              v-model="search"
              placeholder="Search screens"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="pos-search"
            />
            <v-spacer />
            <v-btn text small color="primary" @click="setAll('full')">
              <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>Full access to all
            </v-btn>
            <v-btn text small @click="setAll('view')">
              <v-icon left small>mdi-eye-outline</v-icon>View only for all
            </v-btn>
            <v-btn text small @click="setAll('none')">
              <v-icon left small>mdi-checkbox-multiple-blank-outline</v-icon>Clear all
            </v-btn>
          </div>

          <!-- Sections -->
          <v-row dense>
            <v-col v-for="section in visibleSections" :key="section.key" cols="12" md="6" xl="4">
              <div class="pos-section">
                <div class="pos-section__head">
                  <v-simple-checkbox
                    :value="sectionState(section) === 'all'"
                    :indeterminate="sectionState(section) === 'some'"
                    color="primary"
                    :ripple="false"
                    @input="setSection(section, $event ? 'full' : 'none')"
                  />
                  <span class="pos-section__title">{{ section.title }}</span>
                  <v-spacer />
                  <span class="pos-section__count">
                    {{ section.modules.filter((m) => selected[m.key] !== "none").length }}/{{ section.modules.length }}
                  </span>
                </div>

                <div
                  v-for="mod in section.modules"
                  :key="mod.key"
                  class="pos-module"
                  :class="{ 'is-on': selected[mod.key] !== 'none' }"
                >
                  <div class="pos-module__text">
                    <div class="pos-module__name">{{ mod.name }}</div>
                    <div class="pos-module__desc">{{ mod.description }}</div>
                    <v-btn
                      v-if="mod.key === 'reports' && access.reports && selected.reports !== 'none'"
                      x-small
                      text
                      color="primary"
                      class="px-0 mt-1"
                      @click="reportDialog = true"
                    >
                      <v-icon left x-small>mdi-format-list-checks</v-icon>
                      Choose reports ({{ reportCount }} of {{ reportTotal }})
                    </v-btn>
                  </div>
                  <v-select
                    v-model="selected[mod.key]"
                    :items="levelItems(mod)"
                    dense
                    outlined
                    hide-details
                    class="pos-level"
                    :menu-props="{ offsetY: true }"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="pos-level-dot" :class="`is-${item.value}`"></span>
                      <span class="pos-level__text">{{ item.text }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <span class="pos-level-dot" :class="`is-${item.value}`"></span>
                      <span>{{ item.text }}</span>
                    </template>
                  </v-select>
                </div>
              </div>
            </v-col>
          </v-row>

          <div v-if="!visibleSections.length" class="pos-muted text-center py-6">
            No screens match "{{ search }}"
          </div>
        </template>
      </section>

      <v-alert v-if="error" type="error" text dense class="mt-4">{{ error }}</v-alert>

      <!-- Which reports this position can open -->
      <v-dialog v-if="access && access.reports" v-model="reportDialog" max-width="1100" scrollable>
        <v-card>
          <v-card-title class="pos-dialog__title">
            <div>
              <div>Reports a {{ titleCase(form.name) }} can open</div>
              <div class="pos-muted font-weight-regular">
                Only reports that show their own {{ form.name === "MASTER FRANCHISEE" ? "zone" : "franchise" }}'s
                data are listed here. Admin-only reports cannot be given.
              </div>
            </div>
            <v-spacer />
            <div class="pos-count"><strong>{{ reportCount }}</strong> of {{ reportTotal }} reports</div>
          </v-card-title>
          <div class="pos-toolbar px-6 my-0 pb-3">
            <v-text-field
              v-model="reportSearch"
              placeholder="Search reports"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="pos-search"
            />
            <v-spacer />
            <v-btn text small color="primary" @click="setReports(visibleReportCategories, true)">
              <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon>Select all
            </v-btn>
            <v-btn text small @click="setReports(visibleReportCategories, false)">
              <v-icon left small>mdi-checkbox-multiple-blank-outline</v-icon>Clear all
            </v-btn>
          </div>
          <v-divider />
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col v-for="cat in visibleReportCategories" :key="cat.key" cols="12" md="6">
                <div class="pos-section">
                  <div class="pos-section__head">
                    <v-simple-checkbox
                      :value="reportState(cat) === 'all'"
                      :indeterminate="reportState(cat) === 'some'"
                      color="primary"
                      :ripple="false"
                      @input="setReports([cat], $event)"
                    />
                    <v-icon small color="primary">{{ cat.icon }}</v-icon>
                    <span class="pos-section__title">{{ cat.title }}</span>
                    <v-spacer />
                    <span class="pos-section__count">
                      {{ cat.reports.filter((r) => reportSel[r.key]).length }}/{{ cat.reports.length }}
                    </span>
                  </div>
                  <div
                    v-for="r in cat.reports"
                    :key="r.key"
                    class="pos-report"
                    :class="{ 'is-on': reportSel[r.key] }"
                    @click="reportSel[r.key] = !reportSel[r.key]"
                  >
                    <v-simple-checkbox
                      :value="reportSel[r.key]"
                      color="primary"
                      :ripple="false"
                      @input="reportSel[r.key] = $event"
                      @click.native.stop
                    />
                    <div class="pos-module__text">
                      <div class="pos-module__name">{{ r.title }}</div>
                      <div class="pos-module__desc">{{ r.description }}</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
            <div v-if="!visibleReportCategories.length" class="pos-muted text-center py-6">
              No reports match "{{ reportSearch }}"
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions class="px-6 py-3">
            <span class="pos-muted">Changes are saved with "Save Changes".</span>
            <v-spacer />
            <v-btn color="primary" depressed @click="reportDialog = false">Done</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

const LEVEL_INFO = {
  none: { text: "No access", help: "Hidden from the menu" },
  view: { text: "View only", help: "Can open the screen and look, nothing else" },
  edit: { text: "View + Add & Edit", help: "Can also add new records and edit them" },
  full: { text: "Full access", help: "Can also delete and restore records" },
};

export default {
  name: "PositionEditPage",
  data() {
    return {
      form: { name: "", is_active: false, position_permissions: [] },
      old_json: null,
      access: null,
      selected: {},
      search: "",
      reportSel: {},
      reportSearch: "",
      reportDialog: false,
      error: null,
      isLoading: false,
      isFetching: false,
    };
  },
  computed: {
    positionId() {
      return this.$route.params.positionId;
    },
    isPanelPosition() {
      return !!(this.access && this.access.is_panel_position);
    },
    allModules() {
      return this.access ? this.access.sections.flatMap((s) => s.modules) : [];
    },
    totalCount() {
      return this.allModules.length;
    },
    grantedCount() {
      return this.allModules.filter((m) => this.selected[m.key] !== "none").length;
    },
    reportCategories() {
      return this.access && this.access.reports ? this.access.reports.categories : [];
    },
    reportTotal() {
      return Object.keys(this.reportSel).length;
    },
    reportCount() {
      return Object.values(this.reportSel).filter(Boolean).length;
    },
    visibleReportCategories() {
      const q = (this.reportSearch || "").trim().toLowerCase();
      if (!q) return this.reportCategories;
      return this.reportCategories
        .map((c) => ({
          ...c,
          reports: c.reports.filter((r) => `${c.title} ${r.title} ${r.description}`.toLowerCase().includes(q)),
        }))
        .filter((c) => c.reports.length);
    },
    legend() {
      return Object.keys(LEVEL_INFO).map((value) => ({ value, ...LEVEL_INFO[value] }));
    },
    visibleSections() {
      if (!this.access) return [];
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.access.sections;
      return this.access.sections
        .map((s) => ({
          ...s,
          modules: s.modules.filter((m) =>
            `${s.title} ${m.name} ${m.description}`.toLowerCase().includes(q)
          ),
        }))
        .filter((s) => s.modules.length);
    },
  },
  async mounted() {
    this.isFetching = true;
    try {
      await Promise.all([this.getData(), this.getAccess()]);
    } catch (e) {
      this.error = "Could not load this position. Please go back and try again.";
    } finally {
      this.isFetching = false;
    }
  },
  methods: {
    titleCase(text) {
      return String(text || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    async getData() {
      const { data } = await axios.get(`/positions/${this.positionId}`);
      this.form = data.data;
      this.old_json = JSON.parse(JSON.stringify(data.data));
    },
    async getAccess() {
      const { data } = await axios.get(`/positions/${this.positionId}/panel_access`);
      this.applyAccess(data.data);
    },
    applyAccess(access) {
      this.access = access;
      const selected = {};
      access.sections.forEach((s) => s.modules.forEach((m) => (selected[m.key] = m.level)));
      this.selected = selected;

      const reportSel = {};
      if (access.reports) {
        access.reports.categories.forEach((c) => c.reports.forEach((r) => (reportSel[r.key] = r.granted)));
      }
      this.reportSel = reportSel;
    },
    reportState(category) {
      const on = category.reports.filter((r) => this.reportSel[r.key]).length;
      if (on === 0) return "none";
      return on === category.reports.length ? "all" : "some";
    },
    setReports(categories, value) {
      categories.forEach((c) => c.reports.forEach((r) => (this.reportSel[r.key] = value)));
    },
    levelItems(mod) {
      return mod.levels.map((value) => ({ value, text: LEVEL_INFO[value].text }));
    },
    // The closest level the module offers, without going above the one asked for
    levelFor(mod, level) {
      const order = Object.keys(LEVEL_INFO);
      const fits = mod.levels.filter((l) => order.indexOf(l) <= order.indexOf(level));
      return fits.length ? fits[fits.length - 1] : "none";
    },
    sectionState(section) {
      const on = section.modules.filter((m) => this.selected[m.key] !== "none").length;
      if (on === 0) return "none";
      return on === section.modules.length ? "all" : "some";
    },
    setSection(section, level) {
      section.modules.forEach((m) => (this.selected[m.key] = this.levelFor(m, level)));
    },
    setAll(level) {
      this.visibleSections.forEach((s) => this.setSection(s, level));
    },
    async save() {
      this.isLoading = true;
      this.error = null;
      try {
        // Details first: the details form leaves panel grants alone, and
        // saving access afterwards makes it the final word
        await axios.post(`/positions`, this.form);
        if (this.isPanelPosition) {
          const payload = { modules: this.selected };
          if (this.access.reports) {
            payload.reports = Object.keys(this.reportSel).filter((k) => this.reportSel[k]);
          }
          const { data } = await axios.post(`/positions/${this.positionId}/panel_access`, payload);
          this.applyAccess(data.data);
        }
        await this.saveUserTimestamp(this.old_json, { ...this.form, panel_modules: this.selected });
        this.$router.push("/positions");
      } catch (e) {
        const body = e.response && e.response.data;
        this.error = (body && body.message) || "Could not save the position. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.pos-page {
  max-width: 1400px;
}
.pos-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}
.pos-crumb {
  font-size: 12px;
  font-weight: 600;
  color: var(--pw-brand, #153a6e);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.pos-title {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.pos-card {
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 16px;
}
.pos-card__title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 14px;
}
.pos-muted {
  color: #66738a;
  font-size: 13px;
}
.pos-access-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.pos-count {
  font-size: 13px;
  color: #66738a;
  background: #eef3fb;
  border-radius: 999px;
  padding: 6px 14px;
  white-space: nowrap;
}
.pos-count strong {
  color: var(--pw-brand, #153a6e);
  font-size: 15px;
}
.pos-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 18px 0 12px;
}
.pos-search {
  max-width: 320px;
}
.pos-section {
  border: 1px solid #e4e8ee;
  border-radius: 12px;
  height: 100%;
  overflow: hidden;
}
.pos-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f6f8fb;
  border-bottom: 1px solid #e4e8ee;
}
.pos-section__title {
  font-weight: 700;
  font-size: 14px;
}
.pos-section__count {
  font-size: 12px;
  color: #8d99ad;
}
.pos-module {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f2f6;
  transition: background 0.15s;
}
.pos-module:last-child {
  border-bottom: none;
}
.pos-module:hover {
  background: #fafbfd;
}
.pos-module.is-on {
  background: #f3f7fd;
}
.pos-module__text {
  flex: 1 1 auto;
  min-width: 0;
  cursor: pointer;
}
.pos-module__name {
  font-size: 14px;
  font-weight: 600;
  color: #1a2436;
}
.pos-module__desc {
  font-size: 12px;
  color: #66738a;
}
.pos-legend {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 6px 18px;
  margin-top: 14px;
  font-size: 13px;
}
.pos-legend__item {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.pos-level {
  flex: 0 0 190px;
  max-width: 190px;
  font-size: 13px;
}
.pos-level__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pos-level-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 8px;
  background: #c2cbd8;
}
.pos-level-dot.is-view {
  background: #2a78d6;
}
.pos-level-dot.is-edit {
  background: #eda100;
}
.pos-level-dot.is-full {
  background: #0ca30c;
}
.pos-dialog__title {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  line-height: 1.4;
}
.pos-report {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid #f0f2f6;
  cursor: pointer;
}
.pos-report:last-child {
  border-bottom: none;
}
.pos-report:hover {
  background: #fafbfd;
}
.pos-report.is-on {
  background: #f3f7fd;
}
.pos-empty {
  text-align: center;
  padding: 28px 16px 8px;
}
.pos-empty h3 {
  margin: 8px 0 4px;
  font-size: 16px;
}
</style>
